import * as actionTypes from "../constants/actionTypes";
import * as modelHelper from "../helpers/modelHelper";
import * as componentTypes from "../constants/componentTypes";
import * as orderBy from "../constants/orderBy";
import * as filtering from "../constants/filtering";
import * as config from "../constants/config";
import * as orderStatus from "../constants/orderStatus";

const initialState = {
    activeOrder: null,
    dispensedOrder: null,
    orderList: [],
    notificationOrders: [],
    printData: null,
    tmcData: null,
    infoData: null,
    validation: null,
    sortingCriterion: orderBy.CREATED,
    sortingAsc: false,
    newOrders: -1,
    filter: null,
    filterType: filtering.ALL,
    offset: config.MAX_ORDERS_LIMIT
};

/**
 * Structure of object from toDispense store:
 * - activeOrder {Object}       - selected order from to dispense order list
 * - dispensedOrder {Object}    - currently dispensed order
 * - orderList {Array}          - to dispense order list,
 * - notificationOrders {Array} - to dispense order list with no filters for notification detection
 * - printData {Object}
 * - tmcData {Object}
 * - infoData {Object}
 * - validation {Object}
 * - sortingCriterion {String}
 * - sortingAsc {Boolean}
 * - newOrders {Number}
 * - filter {String}
 * - filterType {String}
 * - offset {Number}
 */
export const toDispenseTabReducer = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.TO_DISPENSE_SAVE_ORDERS:
            if (action.payload.validOrder) {
                return {
                    ...state,
                    orderList: action.payload.orders
                };
            }

            return {
                ...state,
                activeOrder: null,
                printData: null,
                tmcData: null,
                infoData: null,
                validation: null,
                orderList: action.payload.orders
            };
        case actionTypes.TO_DISPENSE_SAVE_ORDERS_WITH_OFFSET:
            let oldOrderList = state.orderList || [];

            return {
                ...state,
                orderList: oldOrderList.concat(action.payload.orders)
            };
        case actionTypes.TO_DISPENSE_SAVE_NOTIFICATION_ORDERS:
            return {
                ...state,
                notificationOrders: action.payload.notificationOrders,
                newOrders: action.payload.newOrdersCounter
            };
        case actionTypes.MENU_ADD_NEW_TO_DISPENSE_TAB:
        case actionTypes.TO_DISPENSE_RESET_NEW_ORDERS:
            return {
                ...state,
                newOrders: 0
            };
        case actionTypes.SAVE_ORDER_STATUS:
            let newOrderList = [];

            state.orderList.forEach((order) => {
                if (order.identifier === action.payload.orderIdentifier) {
                    newOrderList.push(order.updateOrderStatus(action.payload.status, action.payload.updated));
                } else {
                    newOrderList.push(order.clone());
                }
            });

            return {
                ...state,
                orderList: newOrderList
            };
        case actionTypes.TO_DISPENSE_SAVE_PLAIN_ORDER_DETAIL:
            if (
                action.payload.order === null ||
                action.payload.order.status === orderStatus.ORDER_STATUS_DONE ||
                action.payload.order.status === orderStatus.ORDER_STATUS_CANCELED
            ) {
                return {
                    ...state,
                    activeOrder: null,
                    printData: null,
                    tmcData: null,
                    infoData: null,
                    validation: null
                };
            }

            if (state.orderList.some((order) => order.id === action.payload.order.id)) {
                return {
                    ...state,
                    activeOrder: modelHelper.mapPlainOrderDataToObject(action.payload.order, action.payload.database),
                    printData: modelHelper.formatPrintData(action.payload.printData),
                    tmcData: action.payload.tmcData,
                    infoData: action.payload.infoData,
                    validation: action.payload.validation
                };
            }

            const orderObject = modelHelper.mapOrderToObject(action.payload.order);

            return {
                ...state,
                activeOrder: modelHelper.mapPlainOrderDataToObject(action.payload.order, action.payload.database),
                printData: modelHelper.formatPrintData(action.payload.printData),
                tmcData: action.payload.tmcData,
                infoData: action.payload.infoData,
                validation: action.payload.validation,
                orderList: orderObject === null ? state.orderList : [...state.orderList, orderObject]
            };
        case actionTypes.TO_DISPENSE_SAVE_SORTING:
            return {
                ...state,
                sortingCriterion: action.payload.criterion,
                sortingAsc: action.payload.asc
            };
        case actionTypes.TO_DISPENSE_CHANGE_OFFSET:
            return {
                ...state,
                offset: action.payload.offset
            };
        case actionTypes.INTERGAMMA_MACHINE_UPDATE_DATABASE_ACCESS:
            if (state.activeOrder === null) {
                return state;
            }

            return {
                ...state,
                activeOrder: state.activeOrder.updateDatabaseAvailability(action.payload.databaseAvailability)
            };
        case actionTypes.TO_DISPENSE_SAVE_PACKAGE_PREVIEW_IMAGE:
            if (state.activeOrder === null) {
                return state;
            }

            return {
                ...state,
                activeOrder: state.activeOrder.updateImageId(action.payload.imageId)
            };
        case actionTypes.TO_DISPENSE_RESET_ORDER:
            return {
                ...state,
                activeOrder: null,
                printData: null,
                tmcData: null,
                infoData: null,
                validation: null
            };
        case actionTypes.TO_DISPENSE_SAVE_FORMULA_VALIDATION:
            if (state.activeOrder === null) {
                return state;
            }

            if (state.validation === null) {
                return {
                    ...state,
                    activeOrder: state.activeOrder.updateFormula(
                        state.activeOrder.formula.validateColorants(action.payload.result)
                    )
                };
            }

            return {
                ...state,
                activeOrder: state.activeOrder.updateFormula(
                    state.activeOrder.formula.validateColorants(action.payload.result)
                ),
                validation: state.validation.updateMachineValidationAll(action.payload.result[2])
            };
        case actionTypes.SAVE_MANUAL_FORMULA_TO_DISPENSE:
            if (state.activeOrder === null || state.activeOrder.formula === null) {
                return state;
            }

            return {
                ...state,
                activeOrder: state.activeOrder.updateManualColorantList(
                    modelHelper.mapManualColorantsToObjects(
                        state.activeOrder.formula.realColorantList,
                        action.payload.formula
                    )
                )
            };
        case actionTypes.TO_DISPENSE_SAVE_ORDER_DETAIL:
            if (
                action.payload.order === null ||
                action.payload.order.status === orderStatus.ORDER_STATUS_DONE ||
                action.payload.order.status === orderStatus.ORDER_STATUS_CANCELED
            ) {
                return {
                    ...state,
                    activeOrder: null,
                    printData: null,
                    tmcData: null,
                    infoData: null,
                    validation: null
                };
            }

            let imageId = -1;

            if (state.activeOrder !== null && state.activeOrder.package !== null) {
                imageId = state.activeOrder.package.imageId;
            }

            let orderData = modelHelper.mapOrderDataToObject(action.payload.order);

            if (state.orderList.some((order) => order.id === action.payload.order.id)) {
                return {
                    ...state,
                    activeOrder: orderData === null ? null : orderData.updateImageId(imageId),
                    printData: modelHelper.formatPrintData(action.payload.printData),
                    tmcData: action.payload.tmcData,
                    infoData: action.payload.infoData,
                    validation: action.payload.validation
                };
            }

            const simpleObject = modelHelper.mapOrderToObject(action.payload.order);

            return {
                ...state,
                activeOrder: orderData === null ? null : orderData.updateImageId(imageId),
                printData: modelHelper.formatPrintData(action.payload.printData),
                tmcData: action.payload.tmcData,
                infoData: action.payload.infoData,
                validation: action.payload.validation,
                orderList: simpleObject === null ? state.orderList : [...state.orderList, simpleObject]
            };
        case actionTypes.TO_DISPENSE_START_FORMULA_DISPENSING:
            if (action.payload.order === null) {
                return state;
            }

            return {
                ...state,
                dispensedOrder: action.payload.order
            };
        case actionTypes.TO_DISPENSE_SAVE_DISPENSED_ORDER_NOTE:
            if (state.dispensedOrder === null) {
                return state;
            }

            return {
                ...state,
                dispensedOrder: state.dispensedOrder.updateNote(action.payload.note)
            };
        case actionTypes.POP_DISPENSING_FORMULA:
            return {
                ...state,
                dispensedOrder: null
            };
        case actionTypes.TO_DISPENSE_SAVE_FILTER:
            return {
                ...state,
                filter: action.payload.filter
            };
        case actionTypes.TO_DISPENSE_SAVE_FILTER_TYPE:
            return {
                ...state,
                filterType: action.payload.filterType
            };
        case actionTypes.INTERGAMMA_SET_OVERRIDE_MODE:
            return initialState;
        case actionTypes.TO_DISPENSE_SET_COLORANT_DISPENSING_LOGIC:
            if (state.activeOrder === null) {
                return state;
            }

            return {
                ...state,
                activeOrder: state.activeOrder.updateColorantDispenseLogic(
                    action.payload.colorantName,
                    action.payload.dispensingLogic
                )
            };
        case actionTypes.CLOSE_ACTIVE_TAB:
        case actionTypes.CLOSE_ALL_TABS_EXCEPT_ACTIVE:
        case actionTypes.CLOSE_ALL_TABS:
            let tab;
            for (tab of action.payload.newTabList) {
                if (tab.type === componentTypes.TO_DISPENSE_TAB) {
                    return state;
                }
            }

            return {
                ...initialState,
                dispensedOrder: state.dispensedOrder,
                orderList: state.orderList,
                newOrders: state.newOrders,
                notificationOrders: state.notificationOrders
            };
        case actionTypes.LOG_OUT_SUCCESS:
        case actionTypes.WEBSOCKET_CLOSED:
        case actionTypes.DATABASE_CHANGE:
            return initialState;
        default:
            return state;
    }
};
